import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import App from './App';
import { msalConfig, loginRequest } from './authConfig';
import './index.css';
import './i18n';

const msalInstance = new PublicClientApplication(msalConfig);

// ─── Jeton d'authentification attaché automatiquement à tous les appels API ──
// Le backend vérifie désormais un vrai jeton Entra (Authorization: Bearer)
// sur chaque route -- avant, le frontend n'envoyait JAMAIS ce jeton, donc
// n'importe quel appel réseau vers l'API était accepté sans vérification.
// Plutôt que de modifier chaque appel fetch(`${API}/api/...`) séparément
// dans chaque page (des dizaines de sites), on intercepte fetch() une seule
// fois ici : toute requête vers notre propre API se voit ajouter le jeton
// ID Entra du praticien connecté, sans rien changer dans les pages elles-mêmes.
const API_BASE = process.env.REACT_APP_API_URL || '';

function isOwnApiRequest(input) {
  const url = typeof input === 'string' ? input : (input && input.url) || '';
  if (API_BASE && url.startsWith(API_BASE)) return true;
  if (!API_BASE && url.startsWith('/api/')) return true;
  return false;
}

const _originalFetch = window.fetch.bind(window);
window.fetch = async (input, init = {}) => {
  if (!isOwnApiRequest(input)) {
    return _originalFetch(input, init);
  }
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) {
    // Pas de session MSAL active (ex: appel avant connexion) -- laisse passer
    // sans jeton, le backend renverra 401 comme attendu plutôt que de
    // bloquer ici un appel qui n'a de toute façon aucune chance d'aboutir.
    return _originalFetch(input, init);
  }
  try {
    const result = await msalInstance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
    const headers = new Headers(init.headers || (typeof input !== 'string' ? input.headers : undefined));
    headers.set('Authorization', `Bearer ${result.idToken}`);
    return _originalFetch(input, { ...init, headers });
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      // Session expirée / consentement requis -- redirige vers la connexion
      // plutôt que d'envoyer un appel voué à un 401.
      msalInstance.loginRedirect(loginRequest);
    }
    console.error('AUTH token acquisition failed:', e);
    return _originalFetch(input, init);
  }
};

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <MsalProvider instance={msalInstance}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </MsalProvider>
  </React.StrictMode>
);
